import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Timer } from "lucide-react";
import { api, img } from "../lib/api";
import { THEMES, dealLink } from "../lib/festivals";
import Countdown, { useCountdown } from "./Countdown";

const FX = Array.from({ length: 22 }, (_, i) => ({ left: (i * 37) % 100, top: (i * 53) % 100, delay: (i * 0.7) % 6, size: 4 + ((i * 7) % 9), dur: 5 + ((i * 3) % 6) }));

const Particles = ({ th }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
    {FX.map((p, i) => (
      <span key={i} className={`fx fx-${th.fx}`} style={{ left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: th.fx === "fall" ? p.size * 1.6 : p.size, animationDelay: `${p.delay}s`, animationDuration: `${p.dur}s`, background: th.colors[i % th.colors.length], boxShadow: `0 0 ${p.size * 2}px ${th.colors[i % th.colors.length]}` }} />
    ))}
  </div>
);

function Deal({ d, offset, onEnd }) {
  const th = THEMES[d.theme] || THEMES.bigsale;
  const c = useCountdown(d.ends_at, offset);
  useEffect(() => { if (c.left === 0) onEnd(d.id); }, [c.left, d.id, onEnd]);
  if (c.left === 0) return null;
  const { Icon } = th;
  return (
    <section data-testid={`festival-deal-${d.id}`} className="relative overflow-hidden rounded-[2rem] border border-white/10 rise" style={{ background: th.bg }}>
      <Particles th={th} />
      <Icon aria-hidden className="absolute -right-10 -bottom-12 w-72 h-72 sm:w-96 sm:h-96 opacity-[0.08] rotate-12" style={{ color: th.accent }} />
      <div className="absolute -top-24 -left-24 w-80 h-80 rounded-full blur-3xl opacity-40" style={{ background: th.glow }} />
      <div className="relative grid md:grid-cols-[1.3fr_1fr] gap-8 items-center p-6 sm:p-10 lg:p-12">
        <div className="space-y-5">
          <p className="inline-flex flex-wrap items-center gap-x-2 max-w-full text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.15em] sm:tracking-[0.25em] px-3 py-1.5 rounded-full bg-black/40 backdrop-blur border border-white/10" style={{ color: th.accent }}>
            <Icon size={14} />{th.name} · {th.tag}
          </p>
          <h2 data-testid="festival-deal-title" className="font-display font-black tracking-tight text-3xl sm:text-5xl lg:text-6xl leading-[0.95] break-words [overflow-wrap:anywhere]">{d.title}</h2>
          {d.subtitle && <p className="text-base sm:text-lg text-white/80 max-w-lg">{d.subtitle}</p>}
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-white/70"><Timer size={14} style={{ color: th.accent }} />Offer ends in</p>
            <Countdown c={c} accent={th.accent} />
          </div>
          <Link to={dealLink(d)} data-testid={`festival-deal-cta-${d.id}`} className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-bold text-black transition-transform hover:scale-[1.03] active:scale-95" style={{ background: th.accent, boxShadow: `0 10px 40px -8px ${th.accent}` }}>
            {d.cta_text}<ArrowRight size={18} />
          </Link>
        </div>
        {d.thumbs?.length > 0 && (
          <div className="hidden md:flex justify-center -space-x-10">
            {d.thumbs.map((t, i) => (
              <img key={i} src={img(t)} alt="" className="w-40 lg:w-48 aspect-[4/5] object-cover rounded-3xl border-4 border-black/40 shadow-2xl floaty" style={{ animationDelay: `${i * 0.6}s`, transform: `rotate(${(i - 1) * 6}deg)` }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

export default function FestivalDeals() {
  const [deals, setDeals] = useState([]);
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const load = () => api.get("/festival-deals").then((r) => { setOffset(new Date(r.data.now).getTime() - Date.now()); setDeals(r.data.deals); }).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);
  const onEnd = (id) => setDeals((x) => x.filter((d) => d.id !== id));
  if (!deals.length) return null;
  return (
    <div data-testid="festival-deals" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-8 space-y-6">
      {deals.map((d) => <Deal key={d.id} d={d} offset={offset} onEnd={onEnd} />)}
    </div>
  );
}
